import { useMemo, useState } from "react";
import { Alert } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import TableSkeleton from "../../../components/atoms/table-skeleton/TableSkeleton";
import { FilterBar } from "../../../components/molecule/molecule";
import ProjectHeader from "../../../components/ProjectHeader";
import { DEFAULT_VAT_PERCENTAGE } from "../../../constants/vat-config";
import {
    downloadPlanningPdf,
    useGetProjectPlanningQuery,
} from "../../../store/services/projectPlanningService";
import { selectProjectDetails } from "../../../store/slices/projectDetailsSlice";
import NoPermissionAlert from "../../../components/NoPermissionAlert";
import useProjectPermission from "../../../hooks/useProjectPermission";
import { showToast } from "../../../utils/method";
import { PlanningHierarchy, PlanningSummaryCards } from "./components";
import { PROJECT_AREA, PROJECT_ACTION } from "../../../constant/projectPermissions";
import "./planning.scss";

function ProjectPlanning() {
    const { t, i18n } = useTranslation();
    const { projectId } = useParams();
    const [downloadingPdf, setDownloadingPdf] = useState(false);

    const projectDetails = useSelector(selectProjectDetails);
    const { can, isLoading: permissionLoading } = useProjectPermission();
    const canViewPlanning = !permissionLoading && can(PROJECT_AREA.PLANNING, PROJECT_ACTION.VIEW);
    const canExportPlanning = can(PROJECT_AREA.PLANNING, PROJECT_ACTION.EXPORT);

    const {
        data: planningData,
        isLoading: loading,
        error,
    } = useGetProjectPlanningQuery(projectId, {
        skip: !projectId || !canViewPlanning,
    });

    const hierarchy = planningData?.hierarchy ?? [];
    const summary = planningData?.summary ?? {};
    const totals = planningData?.totals ?? {};
    const projectVat = planningData?.project?.vat ?? DEFAULT_VAT_PERCENTAGE;

    const FILTER_ITEMS = useMemo(
        () =>
            canExportPlanning
                ? [
                      {
                          key: "download",
                          type: "button",
                          labelKey: downloadingPdf
                              ? "common.downloading"
                              : "common.download",
                          displayPosition: "right",
                          className: "btn btn-outline-secondary",
                          icon: "download",
                          disabled: downloadingPdf || loading,
                      },
                  ]
                : [],
        [canExportPlanning, downloadingPdf, loading],
    );

    if (permissionLoading) {
        return (
            <div className="project-planning-page">
                <ProjectHeader projectName={projectDetails?.name} tabTitle={t("planning.title")} />
                <div className="page-container text-center p-5">
                    <TableSkeleton rows={6} columns={5} />
                </div>
            </div>
        );
    }

    if (!canViewPlanning) {
        return (
            <div className="project-planning-page">
                <ProjectHeader projectName={projectDetails?.name} tabTitle={t("planning.title")} />
                <NoPermissionAlert />
            </div>
        );
    }

    if (loading) {
        return (
            <div className="project-planning-page">
                <ProjectHeader projectName={projectDetails?.name} tabTitle={t("planning.title")} />
                <div className="page-container">
                    <div className="ct-skeleton">
                        <div className="ct-skeleton__cards">
                            {[0, 1, 2, 3].map((i) => (
                                <div key={i} className="ct-skeleton__card">
                                    <span className="sk-line" style={{ width: "55%", height: "10px" }} />
                                    <span className="sk-line" style={{ width: "75%", height: "24px" }} />
                                    <span className="sk-line" style={{ width: "40%", height: "9px" }} />
                                </div>
                            ))}
                        </div>
                        <div className="ct-skeleton__table">
                            <TableSkeleton rows={10} columns={5} />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        const errMessage =
            error?.data?.message ||
            error?.message ||
            (typeof error === "string" ? error : t("common.error"));
        return (
            <Alert variant="danger" className="m-4">
                <Alert.Heading>{t("common.error")}</Alert.Heading>
                <p>{errMessage}</p>
            </Alert>
        );
    }

    const onFilterAction = async (index, key) => {
        if (key === "download") {
            try {
                setDownloadingPdf(true);
                const language = i18n.language || "de";
                await downloadPlanningPdf(projectId, language);
                showToast(t("planning.pdf-download-success"), { type: "success" });
            } catch (error) {
                showToast(error?.message || t("planning.pdf-download-error"), { type: "error" });
            } finally {
                setDownloadingPdf(false);
            }
        }
    };

    return (
        <div className="project-planning-page">
            <ProjectHeader
                projectName={projectDetails?.name}
                tabTitle={t("planning.title")}
            />
            <div className="page-container">
                <FilterBar items={FILTER_ITEMS} onFilterAction={onFilterAction} />

                {/* Summary Cards */}
                <PlanningSummaryCards summary={summary} />

                <div className="planning-content">
                    <PlanningHierarchy
                        hierarchy={hierarchy}
                        totals={totals}
                        projectId={projectId}
                        projectVat={projectVat}
                        canEdit={can(PROJECT_AREA.PLANNING, PROJECT_ACTION.EDIT)}
                        canCreate={can(PROJECT_AREA.PLANNING, PROJECT_ACTION.EDIT)}
                        canDelete={can(PROJECT_AREA.PLANNING, PROJECT_ACTION.EDIT)}
                    />
                </div>
            </div>
        </div>
    );
}

export default ProjectPlanning;
